import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from '../components/Icons';
import { db, appId } from '../utils/firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import DetailModal from '../components/DetailModal';
import { safeHtml } from '../utils/safeHtml';

const DashboardView = ({ dbData, userData, onNavigate, isAdmin }) => {
    const [recentAnnouncements, setRecentAnnouncements] = useState([]);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [selectedNews, setSelectedNews] = useState(null);

    const totalDoors = (dbData?.artifacts || []).filter(a => a.designation?.includes('DOOR')).length;
    const totalArtifacts = (dbData?.artifacts || []).filter(a => !a.designation?.includes('DOOR')).length;
    const totalLogs = (dbData?.journals || []).length;

    // Ticking Clock
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Fetch News
    useEffect(() => {
        if (!appId) return;
        const annQ = query(collection(db, 'artifacts', appId, 'public', 'data', 'announcements'), orderBy('date', 'desc'), limit(3));
        const unsubAnn = onSnapshot(annQ, (snap) => setRecentAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        return () => unsubAnn();
    }, []);

    const recentEvents = useMemo(() => {
        const rawTimeline = dbData?.timeline || [];
        const rawJournals = (dbData?.journals || []).filter(j => j.date).map(j => ({ ...j, type: 'journal', track: j.author || 'GLOBAL', event: j.title }));
        const rawLore = (dbData?.ideas || []).filter(i => i.date).map(i => ({ ...i, type: 'lore', track: 'GLOBAL', event: i.title || i.name }));
        const rawArtifacts = (dbData?.artifacts || []).filter(a => a.date).map(a => ({ ...a, type: 'artifact', track: 'GLOBAL', event: a.title || a.name }));
        
        const combined = [...rawTimeline, ...rawJournals, ...rawLore, ...rawArtifacts];
        
        return combined
            .filter(e => e.date && e.date !== 'TBD')
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 5); 
    }, [dbData]);

    const ActionCard = ({ title, action, icon: Icon, colorClass, target }) => (
        <div onClick={() => onNavigate(target)} className="bg-[#0a0a0a] border border-gray-800 p-5 rounded shadow-lg hover:border-white cursor-pointer transition-all group flex flex-col h-full min-h-[140px]">
            <div className={`p-3 rounded bg-black border border-gray-800 mb-4 w-fit group-hover:scale-110 transition-transform ${colorClass}`}>
                <Icon size={20} />
            </div>
            <h3 className="text-gray-500 font-bold text-[10px] uppercase tracking-widest mb-1">{action}</h3>
            <p className="text-white font-black text-xs uppercase leading-snug">{title}</p>
            <div className="mt-auto pt-4 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity text-gray-500">
                <Icons.ArrowLeft className="rotate-180" size={16} />
            </div>
        </div>
    );

    const StatCard = ({ title, count, icon: Icon, colorClass }) => (
        <div className={`bg-[#0a0a0a] border border-gray-800 p-5 rounded shadow-lg relative overflow-hidden group hover:border-${colorClass.split('-')[1]}-900/50 transition-colors`}>
            <div className={`absolute -right-4 -top-4 opacity-5 ${colorClass}`}><Icon size={100} /></div>
            <div className="relative z-10 flex items-center justify-between">
                <div>
                    <h3 className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1">{title}</h3>
                    <div className={`text-4xl font-black ${colorClass} tracking-tighter`}>{count}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex-1 overflow-y-auto custom-scrollbar bg-[#050505] fade-in h-full pb-20">
            
            <style>{`
                .rich-text-widget p { display: inline; margin-right: 0.25rem; }
                .rich-text-widget strong { color: white; font-weight: 800; }
                .rich-text-widget h1, .rich-text-widget h2, .rich-text-widget h3 { display: inline; font-weight: 800; color: white; font-size: 1em; margin-right: 0.5rem; }
            `}</style>

            <div className="p-8 border-b border-gray-900 bg-black relative overflow-hidden">
                <div className="absolute inset-0 map-grid-bg opacity-20 pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span></span>
                            <span className="text-[10px] font-bold text-green-500 uppercase tracking-widest">SYSTEM V1.6.0 LIVE</span>
                        </div>
                        <h2 className="text-3xl font-black text-white uppercase tracking-tighter">Mission Control</h2>
                        <p className="text-sm text-gray-500 font-mono mt-1">Welcome back, Operative {userData?.handle}</p>
                    </div>
                    <div className="text-right bg-[#0a0a0a] border border-gray-900 p-4 rounded shadow-xl hidden md:block">
                        <div className="text-[10px] text-gray-600 font-bold uppercase tracking-widest mb-1">Current Sync Time</div>
                        <div className="text-lg text-cyan-500 font-mono">{currentTime.toLocaleTimeString('en-US', { hour12: false })} <span className="text-gray-600 text-sm">PST</span></div>
                    </div>
                </div>
            </div>

            <div className="p-6 md:p-8 space-y-10 max-w-7xl mx-auto">
                <div>
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4 flex items-center gap-2"><Icons.Activity size={14} /> What do you need to do?</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <ActionCard action="Logistics" title="Track physical props" icon={Icons.Database} colorClass="text-blue-500" target="inventory" />
                        <ActionCard action="Geospatial" title="Drop a GPS payload" icon={Icons.MapPin} colorClass="text-cyan-500" target="map" />
                        <ActionCard action="Narrative" title="Log a story event" icon={Icons.Activity} colorClass="text-purple-500" target="timeline" />
                        <ActionCard action="Messaging" title="Check secure comms" icon={Icons.Mail} colorClass="text-indigo-500" target="comms" />
                        <ActionCard action="Cryptography" title="Generate a sigil" icon={Icons.PenTool} colorClass="text-red-500" target="cipher" />
                        <ActionCard action="Stringboard" title="Map connections" icon={Icons.Network} colorClass="text-green-500" target="matrix" />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatCard title="Archived Doors" count={totalDoors} icon={Icons.DoorOpen} colorClass="text-purple-500" />
                    <StatCard title="Secured Artifacts" count={totalArtifacts} icon={Icons.Box} colorClass="text-orange-500" />
                    <StatCard title="Intercepted Logs" count={totalLogs} icon={Icons.BookOpen} colorClass="text-yellow-500" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-10">
                    <div className="lg:col-span-2 flex flex-col gap-8">
                        <div className="bg-[#0a0a0a] border border-gray-800 shadow-xl flex flex-col rounded">
                            <div className="p-4 border-b border-gray-900 bg-black/50 flex items-center justify-between rounded-t">
                                <h3 className="text-sm font-bold text-white uppercase tracking-widest flex items-center gap-2"><Icons.Activity size={16} className="text-blue-500" /> Timeline Pulse</h3>
                                <button onClick={() => onNavigate('timeline')} className="text-[10px] text-gray-500 hover:text-white uppercase font-bold tracking-widest transition-colors">View All</button>
                            </div>
                            <div className="p-4 flex-1">
                                {recentEvents.length === 0 ? <div className="text-xs text-gray-600 font-mono text-center p-6">NO RECENT EVENTS</div> : (
                                    <div className="space-y-4">
                                        {recentEvents.map(evt => (
                                            <div key={evt.id} className={`border-l-2 pl-4 py-1 ${evt.isAnomaly ? 'border-purple-500' : 'border-blue-900/50'}`}>
                                                <div className="flex gap-2 items-center mb-1">
                                                    <span className="text-[9px] text-gray-500 font-mono bg-black px-1.5 py-0.5 border border-gray-800 rounded">{evt.date}</span>
                                                    <span className={`text-[9px] font-bold uppercase tracking-widest ${evt.isAnomaly ? 'text-purple-500 animate-pulse' : 'text-blue-500'}`}>
                                                        {evt.isAnomaly ? 'ANOMALY' : (evt.track || 'GLOBAL')}
                                                    </span>
                                                </div>
                                                <div className="text-sm font-bold text-gray-200">{evt.title || evt.event}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-[#0a0a0a] border border-gray-800 shadow-xl flex flex-col rounded">
                            <div className="p-4 border-b border-gray-900 bg-black/50 flex items-center justify-between rounded-t">
                                <h3 className="text-sm font-bold text-white uppercase tracking-widest flex items-center gap-2"><Icons.MessageSquare size={16} className="text-yellow-500" /> Public Updates Blog</h3>
                                <button onClick={() => onNavigate('announcements')} className="text-[10px] text-gray-500 hover:text-white uppercase font-bold tracking-widest transition-colors">Manage</button>
                            </div>
                            <div className="p-4 flex-1">
                                {recentAnnouncements.length === 0 ? <div className="text-xs text-gray-600 font-mono text-center p-6">NO RECENT ANNOUNCEMENTS</div> : (
                                    <div className="space-y-4">
                                        {recentAnnouncements.map(post => (
                                            <div key={post.id} onClick={() => setSelectedNews(post)} className="bg-black border border-gray-900 p-3 rounded cursor-pointer hover:border-yellow-500/50 transition-colors">
                                                <div className="flex justify-between items-start mb-2">
                                                    <div className="text-xs font-bold text-gray-200 truncate pr-2">{post.title || 'UNTITLED POST'}</div>
                                                    <div className="text-[9px] text-yellow-400 font-mono whitespace-nowrap">{post.date}</div>
                                                </div>
                                                <div 
                                                    className="text-xs text-gray-500 font-sans line-clamp-2 leading-relaxed rich-text-widget" 
                                                    dangerouslySetInnerHTML={{ __html: safeHtml(post.text) }} 
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-900/10 border border-blue-900/30 rounded shadow-xl p-6 relative overflow-hidden flex flex-col">
                        <div className="absolute top-0 right-0 p-4 opacity-5 text-blue-500 pointer-events-none"><Icons.Database size={120}/></div>
                        <div className="text-[10px] text-blue-500 font-bold uppercase tracking-[0.2em] mb-2 relative z-10">PATCH NOTES</div>
                        <h3 className="text-2xl font-black text-white mb-4 tracking-tight uppercase relative z-10">1.6.0 Release</h3>
                        
                        <div className="space-y-4 text-xs text-gray-400 font-sans leading-relaxed relative z-10 flex-1">
                            <p>Tertiary Hub v1.6.0 establishes the platform as a true Headless CMS and CDN for external game engines.</p>
                            
                            <div className="border-l-2 border-indigo-500/50 pl-3">
                                <strong className="text-white block mb-1">Multimedia CDN Integration</strong>
                                You can now upload .mp4 video files and .mp3/.wav audio files directly to Artifacts and Assets, complete with in-hub media players.
                            </div>

                            <div className="border-l-2 border-yellow-500/50 pl-3">
                                <strong className="text-white block mb-1">Board Wipes & GPS Cleanses</strong>
                                The Map module now features a "Clear Board" function to instantly un-anchor all active AR triggers for a fresh testing session.
                            </div>

                            <div className="border-l-2 border-[#00ff41]/50 pl-3">
                                <strong className="text-white block mb-1">Global Lightbox Explorer</strong>
                                Click any image attached to a timeline event, comms thread, or artifact to open the new fullscreen, high-resolution media viewer.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {selectedNews && (
                <DetailModal item={selectedNews} type="NEWS POST" isAdmin={isAdmin} onClose={() => setSelectedNews(null)} onEdit={(item) => { setSelectedNews(null); onNavigate('announcements'); }} />
            )}
        </div>
    );
};

export default DashboardView;